import ReactApexChart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";

interface Props {
    title: string;
    rows: any[];
    valueKey: string;
    seriesName: string;
    dateKey?: string;
    curve?: "smooth" | "straight";
}

export default function LineChart({ title, rows, valueKey, seriesName, dateKey = "week_start", curve = "smooth" }: Props) {
    const data = rows.map((r: any) => ({
        x: new Date(r[dateKey]).getTime(),
        y: Number(r[valueKey] ?? 0),
    }));

    const options: ApexOptions = {
        chart: {
            height: 350,
            type: "line",
            zoom: { enabled: true, type: "x", autoScaleYaxis: true },
            toolbar: { show: true, tools: { download: false, pan: true, zoom: true, zoomin: true, zoomout: true, reset: true, selection: false } },
        },
        dataLabels: {
            enabled: true,
            // Label the first point and any point whose value changed from the
            // prior one — flat runs collapse to a single label, steps stay annotated.
            formatter: (val, opts) => {
                const series = opts.w.globals.series[opts.seriesIndex];
                const i = opts.dataPointIndex;
                return i === 0 || val !== series[i - 1] ? `${val}` : "";
            },
            style: { fontSize: "11px", fontWeight: 600 },
            background: { enabled: true, foreColor: "#fff", borderRadius: 4, padding: 4, borderWidth: 0, opacity: 1 },
        },
        stroke: { curve },
        // Hide per-point dots (they merge into mud at long ranges); keep a hover size for the tooltip.
        markers: { size: 0, hover: { size: 5 } },
        grid: { row: { colors: ["#f3f3f3", "transparent"], opacity: 0.5 } },
        // datetime axis auto-thins ticks (months across a year, weeks across a
        // month) instead of cramming one label per point like a category axis.
        xaxis: { type: "datetime" },
        tooltip: { x: { format: "MM/dd/yy" } },
    };

    return (
        <div className="dash-card">
            <div className="dash-card__head">
                <span className="dash-card__title">{title}</span>
            </div>
            <div className="dash-card__body">
                {rows.length === 0 ? (
                    <div className="dash-empty">No data yet.</div>
                ) : (
                    <ReactApexChart
                        options={options}
                        series={[{ name: seriesName, data }]}
                        type="line"
                        height={300}
                    />
                )}
            </div>
        </div>
    );
}
